import { useState, useMemo, useContext } from "react";

import { i18n } from "../translate/i18n";

import { TenantContext } from "../templates/context/TenantContext";

import { useOtb } from "../model/useOtb";

import { useActiveStore } from "./stores/storeInformationStore";

import { CellValue, SeverityPillCell, Header, SubHeader } from "../components/SharedComponents/sharedComponents.index";
import { OPPORTUNITY_STATUS, ORDERING_MODES, SEASONS_NAMES_DEFAULT } from "../components/components.constants";

import { createPercentFormatter, createIntFormatter, textFormatter } from "../utils/formatter/formatter";
import { DeliveryIcon, CartIcon, GridIcon } from "../modules/Products/AdjustmentsSupply/components/icons.index";

const { t } = i18n;

const getInventoryGapSeverity = (value) => {
  if (value >= 150) return 'red';
  if (value >= 100) return 'orange';
  return 'yellow';
};

const getLostSalesRiskSeverity = (value) => {
  if (value >= 15000) return 'red';
  if (value >= 10000) return 'orange';
  return 'yellow';
};

const getCapitalTiedUpSeverity = (value) => {
  if (value >= 250000) return 'red';
  if (value >= 150000) return 'orange';
  return 'yellow';
};

const analyseOption = {
  id: 1,
  title: t("opportunityWillBeAnalysed"),
  text: t("opportunityWillCheckout"),
  status: OPPORTUNITY_STATUS.CHECKOUT,
  confirmationMessage: t("opportunityHasGoneCheckout"),
};

const removeOption = {
  id: 2,
  title: t("notAnOpportunity"),
  text: t("describeDeletionReason"),
  status: OPPORTUNITY_STATUS.REMOVED,
  confirmationMessage: t("opportunityHasBeenRemoved"),
};

const removeFromCheckoutOption = {
  id: 3,
  title: t("removeFromCheckout"),
  text: t("opportunityWillRemainInList"),
  status: OPPORTUNITY_STATUS.ACTIVE,
  confirmationMessage: t("opportunityHasLeftCheckout"),
};

const finalizeOption = {
  id: 4,
  title: t("finalizeOpportunityOption"),
  text: t("finalizeOpportunityOptionText"),
  status: OPPORTUNITY_STATUS.FINISHED,
  confirmationMessage: t("opportunityHasBeenFinished"),
};

const getOptions = (status) => {
  const standardOptions = [analyseOption, removeOption, finalizeOption];
  const checkoutOptions = [removeFromCheckoutOption, removeOption];
  return status === "checkout" ? checkoutOptions : standardOptions;
};

const generateProductHierarchyInfoForColumnAndLabel = (hierarchy, additionalLevels = {}) => {
  if (!additionalLevels) return null;

  const levels = Object.keys(additionalLevels)
    .sort((a, b) => {
      const numA = parseInt(a.replace('level', ''));
      const numB = parseInt(b.replace('level', ''));
      return numA - numB;
    });

  const columnLevel = levels.pop();

  return {
    columnName: hierarchy?.labels[columnLevel],
    hierarchyLevels: levels.map((key) => additionalLevels[key]).filter(Boolean)
  }
};

export const useOtbViewModel = ({
  shouldFecthOppCounter,
  shouldFetchActiveOpp, 
  shouldFetchCheckoutOpp,
  shouldFetchRemovedOpp,
  shouldFetchInventory,
  shouldFetchSupplyAdjustments,
  shouldFetchOtbConsolidated,
  page, 
  pageSize,
  shouldFetchOtbDetail,
  shouldFetchOtbCycles,
  shouldFetchOtbPerformanceByLevel,
  shouldFetchInventoryDiagnosis,
  shouldFetchSalesPlan,
  productHierarchyNodeId,
  parentLevelId,
  cycleId,
  performanceCycleId,
  shouldFetchOpportunitiesDetails,
  shouldFetchMerchandiseAnalytics,
  levelName }) => {

  const [selectedCategoryName, setSelectedCategoryName] = useState(null);

  const {
    // oportunidades
    updateOpportunityStatusAsync,
    isUpdatingOpportunityStatus,

    checkoutOpportunities,
    isLoadingCheckoutOpportunities,
    checkoutOpportunitiesError,

    toggleTypeFilter,

    activeOpportunities,
    isLoadingActiveOpportunities,
    activeOpportunitiesError,

    removedOpportunities,

    opportunitiesCount,
    isLoadingOppCount,

    opportunitiesTrends,
    isLoadingOpportunitiesTrends,

    opportunitiesTrendsDetails,
    isLoadingOpportunitiesTrendsDetails,

    opportunitiesDetails,
    isLoadingOpportunitiesDetails,

    merchandiseAnalytics,
    isLoadingMerchandiseAnalytics,
    merchandiseAnalyticsError,

    // estoques
    inventory,

    purchaseOperation,
    inventoryDiagnosis,
    isLoadingInventoryDiagnosis,

    // abastecimento
    fetchSupplyAdjustmentsData,
    isLoadingSupplyAdjustments,
    storesBreakdown,

    // open to buy
    openToBuyDataConsolidated,
    isLoadingOtbDataConsolidated,

    otbDetailByLevel,
    isLoadingOtbDetail,

    otbCycles,
    isLoadingOtbCycles,

    otbPerformanceByLevel,
    isLoadingOtbPerformanceByLevel,

    otbSalesPlan,
    isLoadingOtbSalesPlan,

    otbSalesPlanDetailed,
    isLoadingOtbSalesPlanDetailed,

    saveOtbVersionAsync,
    isSavingOtbVersion,
  } = useOtb({ 
    shouldFetchActiveOpp, 
    shouldFetchCheckoutOpp,
    shouldFetchRemovedOpp,
    shouldFetchInventory, 
    shouldFecthOppCounter,
    shouldFetchSupplyAdjustments,
    shouldFetchOtbConsolidated,
    shouldFetchOtbDetail,
    shouldFetchInventoryDiagnosis,
    shouldFetchOtbCycles,
    shouldFetchOtbPerformanceByLevel,
    shouldFetchSalesPlan,
    productHierarchyNodeId,
    parentLevelId,
    cycleId,
    performanceCycleId,
    shouldFetchOpportunitiesDetails,
    shouldFetchMerchandiseAnalytics,
    page,
    pageSize,
    category: selectedCategoryName,
  });

  const { hierarchy } = useContext(TenantContext);

  const { storeId, setStoreId } = useActiveStore();

  const [selectedId, setSelectedId] = useState(0);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [removalReason, setRemovalReason] = useState("");

  const [order, setOrder] = useState(ORDERING_MODES.DESCENDING);

  const [checkoutSelectedProductIds, setCheckoutSelectedProductIds] = useState(checkoutOpportunities?.productIds || []);
  const [activeProductIds, setActiveOppProductIds] = useState(activeOpportunities?.activeProductIds || []);

  const [oppError, setOppError] = useState(null);

  const [sorting, setSorting] = useState([{
    id: 'best_priority',
    desc: false
  }]);

  const [searchLevel, setSearchLevel] = useState("");
  const [season, setSeason] = useState([]);
  
  const [horizon, setHorizon] = useState(3);

  // open to buy
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [otbSidebarOpen, setOtbSidebarOpen] = useState(true);
  //const [setup, setSetup] = useState(SETUP_DEFAULT);
  const [saveOtbVersionError, setSaveOtbVersionError] = useState(null);

  // oportunidades
  const handleRemoveOpportunity = async (opportunityId, status, removalReason) => {
    const options = getOptions(status);
    const selected = options.find(option => option.id === selectedId);

    if (!selected) {
      setConfirmationMessage(t("errorContactAdmin"));
      return;
    }

    try {
      await updateOpportunityStatusAsync({
        opportunityId,
        status: selected.status,
        removalReason: removalReason
      });
      setConfirmationMessage(selected.confirmationMessage);
    } catch (err) {
      setOppError(err)
    }
  };

  const handleFinishCheckoutOpportunity = async ({ opportunityId, checkoutDescription }) => {
    try {
      await updateOpportunityStatusAsync({
        opportunityId,
        status: OPPORTUNITY_STATUS.FINISHED,
        removalReason: checkoutDescription,
      });
      setConfirmationMessage(t("opportunityHasBeenFinished"));
    } catch (err) {
      setOppError(err)
    }
  };
  
  const resetModalState = () => {
    setSelectedId(0);
    setConfirmationMessage("");
    setRemovalReason("");
  };

  const selected = checkoutOpportunities?.checkoutOpportunities
    ? checkoutOpportunities.checkoutOpportunities.find(opportunity => opportunity.opportunityId === selectedId) 
    : undefined;
  
  
  const availableTypes = useMemo(() => {
    const types = activeOpportunities?.opportunityTypes ?? {};
    return Object.keys(types).map(type => ({
      value: type,
      displayName: types[type]?.opportunitieDescriptionType1Pt || type
    }));
  }, [activeOpportunities?.opportunityTypes]);

  const availableCheckoutTypes = useMemo(() => {
    const types = checkoutOpportunities?.opportunityTypes ?? {};
    return Object.keys(types).map(type => ({
      value: type,
      displayName: types[type]?.opportunitieDescriptionType1Pt || type
    }));
  }, [checkoutOpportunities?.opportunityTypes]);

  const sortedActiveOpportunities = useMemo(() => {
    const filteredByTypes = activeOpportunities?.activeOpportunities
      ? activeOpportunities.activeOpportunities.filter(opp => activeOpportunities?.types.includes(opp.type1))
      : [];

    const filteredByHierarchy = filteredByTypes.filter(opp => {
      const productIds = activeProductIds ?? [];
      if (productIds.length === 1) return opp.productId === productIds[0];
      return productIds.length === 0 || productIds.includes(opp.productId);
    });

    return [...filteredByHierarchy].sort((a, b) => {
      if (order === ORDERING_MODES.ASCENDING) return a.value - b.value;
      return b.value - a.value;
    });
  }, [activeOpportunities?.activeOpportunities, activeOpportunities?.types, activeProductIds, order]);

  const sortedCheckoutOpportunities = useMemo(() => {
    const filteredByTypes = checkoutOpportunities?.checkoutOpportunities
      ? checkoutOpportunities.checkoutOpportunities.filter(opp => checkoutOpportunities?.types.includes(opp.type1))
      : [];

    const filteredByHierarchy = filteredByTypes.filter(opp => {
      const productIds = checkoutSelectedProductIds || [];
      return productIds.length === 0 || productIds.includes(opp.productId);
    });

    return [...filteredByHierarchy].sort((a, b) => {
      if (order === ORDERING_MODES.ASCENDING) return a.value - b.value;
      return b.value - a.value;
    });
  }, [checkoutOpportunities?.checkoutOpportunities, checkoutOpportunities?.types, checkoutSelectedProductIds, order]);  

  const sortedRemovedOpportunities = useMemo(() => {
    const filteredByTypes = removedOpportunities?.removedOpportunities
      ? removedOpportunities.removedOpportunities.filter(opp => removedOpportunities?.types.includes(opp.type1))
      : [];

    return [...filteredByTypes].sort((a, b) => {
      if (order === ORDERING_MODES.ASCENDING) return a.value - b.value;
      return b.value - a.value;
    });
  }, [removedOpportunities?.removedOpportunities, removedOpportunities?.types, order]);

  const opportunityCounterData = useMemo(() => {
    const opportunitiesArray = sortedActiveOpportunities || []; 
    const opportunityTypes = activeOpportunities?.opportunityTypes ?? {};

    const counterMap = {};

    for (const opp of opportunitiesArray) {
      if (!counterMap[opp.type1]) {
        counterMap[opp.type1] = { 
          totalValue: 0, 
          count: 0,
          displayName: opportunityTypes[opp.type1]?.opportunitiCounterNamePt || opp.type1
        };
      }

      counterMap[opp.type1].totalValue += Number(opp.value ?? 0);
      counterMap[opp.type1].count += 1;
    }

    const counter = Object.entries(counterMap).map(([type, data]) => ({
      type,
      ...data,
    }));

    const totalValue = counter.reduce((acc, curr) => acc + curr.totalValue, 0);
    const count = counter.reduce((acc, curr) => acc + curr.count, 0);

    return {
      totalValue,
      count,
      counter,
    };
  }, [activeOpportunities, sortedActiveOpportunities]);
  
  const orderOptions = [
    {
      value: ORDERING_MODES.DESCENDING,
      displayName: t("highestValue"),
    },
    {
      value: ORDERING_MODES.ASCENDING,
      displayName: t("lowestValue"),
    },
  ];

  const definePatchOperationType = ({ uiState }) => {
    const { updateOppError }  = uiState || {};

    switch(true) {
      case updateOppError?.config?.method === 'patch': 
        const body = JSON.parse(updateOppError?.config?.data);
        if (body.status === OPPORTUNITY_STATUS.FINISHED) return "patch_finalized_opp"
        else if (body.status === OPPORTUNITY_STATUS.REMOVED) return "patch_removed_opp"
        else return "patch_checkout_opp"
      
      default:
        return "get_opp"
    }
  };

  // abastecimento
  const supplyAdjustmentsColumns = useMemo(() => [
    {
      header: 'Ações',
      id: 'expand',
      cell: info => (
        <div style={{ display: 'flex', justifyContent: 'start', paddingInlineStart: '4%', fontWeight: '500', color: '#374151' }}>
          {""}
        </div>
      ),
      enableSorting: false
    },
    {
      header: 'Áreas de Atuação',
      id: 'areas',
      cell: ({ info, row }) => {
        return (
          <div style={{ display: 'flex', flexDirection: 'row', gap: '0.4rem', inlineSize: 'fit-content' }}>
            <DeliveryIcon uiState={{ hasActions: row?.original?.supplyAdjustmentRepleneshimentArea === 1 }} />
            <CartIcon uiState={{ hasActions: row?.original?.supplyAdjustmentPurchaseArea === 1 }} />
            <GridIcon uiState={{ hasActions: row?.original?.supplyAdjustmentAssortmentArea === 1 }} />
          </div>
        )
      },
      enableSorting: false
    },    
    {
      accessorFn: row => row.level,
      id: 'level',
      header: ({ column, table }) => {
        const firstRow = table.getCoreRowModel().flatRows[0];

        const { columnName } =
          generateProductHierarchyInfoForColumnAndLabel(
            hierarchy,
            firstRow?.original?.additionalLevels ?? []
          );

        return (
          <SubHeader
            ctx={{
              column,
              title: textFormatter(columnName),
              tooltip: t("familyProduct")
            }}
            uiState={{
              sorting,
              showSortButton: true,
              headerFontSize: "1rem"
            }}
          />
        );
      },
      cell: ({ getValue, row }) => {
        const { hierarchyLevels } = generateProductHierarchyInfoForColumnAndLabel(hierarchy, row.original.additionalLevels);

        return (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'left', inlineSize: 'fit-content' }}>
            <CellValue align="left" styles={{ fontSize: '1rem', fontWeight: 700 }} value={getValue()} />
            <span style={{ fontSize: '0.8rem', inlineSize: 'fit-content' }} >
              {textFormatter(hierarchyLevels, { separator: " | " })}
            </span>
          </div>
        );
      },
      enableSorting: true
    },
    {
      header: ({ column }) =>
        <SubHeader
        ctx={{
          column: column,
          title: t("priority"),
          tooltip: t("higherLikehoodOfSale")
        }}
        uiState={{ sorting: sorting, showSortButton: true, headerFontSize: "1rem" }}/>,
      accessorFn: row => row.priority,
      id: 'best_priority',
      cell: ({ getValue }) => <CellValue value={getValue()} />,
      enableSorting: true
    },
    {
      id: "curva",
      header: <Header ctx={{ title: [t("salesBreakdown")] }} uiState={{ headerFontSize: "1rem" }} />,
      columns: [
        {
          accessorFn: row => row.curveA_avg,
          id: 'curveA_avg',
          header: ({ column }) =>
            <SubHeader
            ctx={{
              column: column,
              title: "A",
              tooltip: t("salesBreakdown_A")
            }}
            uiState={{ sorting: sorting, showSortButton: true, headerFontSize: "1rem" }}/>,
            cell: ({ getValue }) => {
              const originalValue = Number(getValue());

              if (originalValue === null) {
                return (
                  <CellValue
                    value="--"
                    originalValue={t("noItemsForCurveBreak")}
                  />
                );
              }

              const value = createPercentFormatter().format(originalValue);

              return <CellValue value={value} />;
            },
          enableSorting: true
        },
        {
          accessorFn: row => row.curveB_avg,
          id: 'curveB_avg',
          header: ({ column }) =>
            <SubHeader
            ctx={{
              column: column,
              title: "B",
              tooltip: t("salesBreakdown_B")
            }}
            uiState={{ sorting: sorting, showSortButton: true, headerFontSize: "1rem" }}/>,
            cell: ({ getValue }) => {
              const originalValue = Number(getValue());

              if (originalValue === 0) {
                return (
                  <CellValue
                    value="--"
                    originalValue={t("noItemsForCurveBreak")}
                  />
                );
              }

              const value = createPercentFormatter().format(originalValue);

              return <CellValue value={value} />;
            },

          enableSorting: true
        },
        {
          accessorFn: row => row.curveC_avg,
          id: 'curveC_avg',
          header: ({ column }) =>
            <SubHeader
            ctx={{
              column: column,
              title: "C",
              tooltip: t("salesBreakdown_C")
            }}
            uiState={{ sorting: sorting, showSortButton: true, headerFontSize: "1rem" }}/>,
            cell: ({ getValue }) => {
              const originalValue = Number(getValue());

              if (originalValue === 0) {
                return (
                  <CellValue
                    value="--"
                    originalValue={t("noItemsForCurveBreak")}
                  />
                );
              }

              const value = createPercentFormatter().format(originalValue);

              return <CellValue value={value} />;
            },
          enableSorting: true
        }
      ]
    },
    {
      header: <Header ctx={{ title: [t("priceRange")] }} uiState={{ headerFontSize: "1rem" }} />,
      id: "faixa_de_preco",
      columns: [
        {
          accessorFn: row => row.priceHigh_avg,
          id: 'priceHigh_avg',
          header: ({ column }) =>
            <SubHeader
            ctx={{
              column: column,
              title: t("high"),
              tooltip: t("priceRange_high")
            }}
            uiState={{ sorting: sorting, showSortButton: true, headerFontSize: "1rem" }}/>,
            cell: ({ getValue }) => {
              const originalValue = Number(getValue());

              if (originalValue === 0) {
                return (
                  <CellValue
                    value="--"
                    originalValue={t("noItemsForPriceRange")}
                  />
                );
              }

              const value = createPercentFormatter().format(originalValue);

              return <CellValue value={value} />;
            },
          enableSorting: true
        },
        {
          accessorFn: row => row.priceMedium_avg,
          id: 'priceMedium_avg',
          header: ({ column }) =>
            <SubHeader
            ctx={{
              column: column,
              title: t("medium"),
              tooltip: t("priceRange_medium")
            }}
            uiState={{ sorting: sorting, showSortButton: true, headerFontSize: "1rem" }}/>,
            cell: ({ getValue }) => {
              const originalValue = Number(getValue());

              if (originalValue === 0) {
                return (
                  <CellValue
                    value="--"
                    originalValue={t("noItemsForPriceRange")}
                  />
                );
              }

              const value = createPercentFormatter().format(originalValue);

              return <CellValue value={value} />;
            },
          enableSorting: true
        },
        {
          accessorFn: row => row.priceLow_avg,
          id: 'priceLow_avg',
          header: ({ column }) =>
            <SubHeader
            ctx={{
              column: column,
              title: t("low"),
              tooltip: t("priceRange_low")
            }}
            uiState={{ sorting: sorting, showSortButton: true, headerFontSize: "1rem" }}/>,
            cell: ({ getValue }) => {
              const originalValue = Number(getValue());

              if (originalValue === 0) {
                return (
                  <CellValue
                    value="--"
                    originalValue={t("noItemsForPriceRange")}
                  />
                );
              }

              const value = createPercentFormatter().format(originalValue);

              return <CellValue value={value} />;
            },
          enableSorting: true
        }
      ]
    },
    {
      header: <Header ctx={{ title: [t("inventoryDetails")] }} uiState={{ headerFontSize: "1rem" }} />,
      id: "estoque_details",
      columns: [
        {
          accessorFn: row => row.avgInventoryCD,
          id: 'avgInventoryCD',
          header: ({ column }) =>
            <SubHeader
            ctx={{
              column: column,
              title: t("quantityCD"),
              tooltip: t("quantityCDDescription")
            }}
            uiState={{ sorting: sorting, showSortButton: true, headerFontSize: "1rem" }}/>,
        cell: ({ getValue }) => <CellValue value={getValue()} />,
        enableSorting: true
        },
        {
          accessorFn: row => row.inventoryStore,
          id: 'inventoryStore',
          header: ({ column }) =>
            <SubHeader
            ctx={{
              column: column,
              title: t("quantityStore"),
              tooltip: t("quantityStoreDescription")
            }}
            uiState={{ sorting: sorting, showSortButton: true, headerFontSize: "1rem" }}/>,
        cell: ({ getValue }) => <CellValue value={getValue()} />,
          enableSorting: true
        },
        {
          accessorFn: row => row.inventoryGap,
          id: 'inventoryGap',
          header: ({ column }) =>
            <SubHeader
            ctx={{
              column: column,
              title: t("quantityShort"),
              tooltip: t("quantityShortDescription")
            }}
            uiState={{ sorting: sorting, showSortButton: true, headerFontSize: "1rem" }}/>,
          cell: ({ getValue }) => {
            const originalValue = Number(getValue());

            if (originalValue === 0) {
              return (
                <CellValue
                  value="--"
                  originalValue={t("noItemsForColumn")}
                />
              );
            }

            const value = createIntFormatter().format(originalValue);
            return (
              <SeverityPillCell
                ctx={{ value: Number(value), severity: getInventoryGapSeverity(originalValue) }}
                uiState={{ type: 'int' }}
              />
            );
          },
          enableSorting: true
        }
      ]
    },
    {
      accessorFn: row => row.lostSalesRisk,
      id: 'lostSalesRisk',
      header: ({ column }) =>
        <SubHeader
        ctx={{
          column: column,
          title: t("riskSalesLostColumn"),
          tooltip: t("riskSalesLostColumn")
        }}
        uiState={{ sorting: sorting, showSortButton: true, headerFontSize: "1rem" }}/>,
        cell: ({ getValue }) => {
          const originalValue = Number(getValue());

          if (originalValue === 0) {
            return (
              <CellValue
                value="--"
                originalValue={t("noItemsForColumn")}
              />
            );
          }

          return <SeverityPillCell ctx={{ value: originalValue, severity: getLostSalesRiskSeverity(originalValue) }} />;
        },
      enableSorting: true
    },
    {
      accessorFn: row => row.capitalTiedUp,
      id: 'capitalTiedUp',
      header: ({ column }) =>
        <SubHeader
        ctx={{
          column: column,
          title: t("capitalTiedUp"),
          tooltip: t("capitalTiedUp")
        }}
        uiState={{ sorting: sorting, showSortButton: true, headerFontSize: "1rem" }}/>,
        cell: ({ getValue }) => {
          const originalValue = Number(getValue());

          if (originalValue === 0) {
            return (
              <CellValue
                value="--"
                originalValue={t("noItemsForColumn")}
              />
            );
          }

          return <SeverityPillCell ctx={{ value: originalValue, severity: getCapitalTiedUpSeverity(originalValue) }} />;
        },
      enableSorting: true
    },
    {
      accessorFn: row => row.inventoryQualityPercentual,
      id: 'inventoryQualityPercentual',
      header: ({ column }) =>
        <SubHeader
        ctx={{
          column: column,
          title: t("inventoryQualityIndex"),
          tooltip: t("inventoryQualityIndex")
        }}
        uiState={{ sorting: sorting, showSortButton: true, headerFontSize: "1rem" }}/>,
        cell: ({ getValue }) => {
          const originalValue = Number(getValue());

          if (originalValue === 0) {
            return (
              <CellValue
                value="--"
                originalValue={t("noItemsForColumn")}
              />
            );
          }

          const value = createPercentFormatter().format(getValue());
          return <CellValue value={value} styles={{ fontWeight: 600 }}/>;
        },
      enableSorting: true
    },
  ], [hierarchy, sorting]);

  const tableData = useMemo(() => {
    return (
      fetchSupplyAdjustmentsData?.supplyAdjustments
        ?.flatMap(item => item.levels)
        .filter(data => {
          const matchesLevel =
            !searchLevel || data.level.toLowerCase().includes(searchLevel.toLowerCase());

            const matchesSeason = !season?.length || season.includes(data.season);

          return matchesLevel && matchesSeason;
        }) || []
    );
  }, [fetchSupplyAdjustmentsData, searchLevel, season]);

  const seasons = useMemo(() => {
    if (!fetchSupplyAdjustmentsData?.supplyAdjustments?.length) 
      return [{ seasonId: 0, seasonName: t("all") }];

    const seasonsSet = fetchSupplyAdjustmentsData.supplyAdjustments.reduce((acc, supplyAdjustment) => {
      supplyAdjustment.levels?.forEach(level => {
        if (level.season) acc.add(level.season);
      });
      return acc;
    }, new Set());

    const seasonsArray = Array.from(seasonsSet);

    return [
      { seasonId: 0, seasonName: SEASONS_NAMES_DEFAULT.ALL_SEASONS },
      ...seasonsArray.map((name, index) => ({
        seasonId: index + 1,
        seasonName: name
      }))
    ];
  }, [fetchSupplyAdjustmentsData]);

  // estoques
  const horizons = useMemo(() => {
    const data = inventory?.data?.inventoryBubbleMetrics?.data || [];

    if (!data.length) return [{ horizon: 0, horizonPeriod: "1 mês" }];

    const horizonSet = data.reduce((acc, item) => {
      if (item.horizon != null) acc.add(item.horizon);
      return acc;
    }, new Set());

    const horizonArray = Array.from(horizonSet).sort((a, b) => a - b);

    return horizonArray.map(h => ({
      horizonId: h,
      horizonPeriod: `${h} mes${h > 1 ? "es" : ""}`
    }));
  }, [inventory?.data?.inventoryBubbleMetrics?.data]);

  const bubbleMetricsData = useMemo(() => {
    return (
      inventory?.data?.inventoryBubbleMetrics?.data
        ?.filter(data => {
          const horizonMatch = !horizon || data.horizon === horizon;
          return horizonMatch;
        }) || []
    );
  }, [inventory, horizon]);

  const mediansForHorizon = inventory?.data?.inventoryBubbleMetrics?.mediansByHorizon?.[horizon] 
    ? [{ key: horizon, ...inventory?.data?.inventoryBubbleMetrics?.mediansByHorizon[horizon] }]
    : [];


  // open to buy
  const handleSaveOtbVersion = async (cycleId, { versionName, edits }) => {
    setSaveOtbVersionError(null);
    try {
      const result = await saveOtbVersionAsync({ cycleId, versionName, edits });
      return result;
    } catch (err) {
      setSaveOtbVersionError(err);
      throw err;
    }
  };

  // const SETUP_DEFAULT = {
  //   periodo: "2025",
  //   canalVenda: "Varejo - Lojas Físicas",
  //   departamento: "Feminino",
  //   metaFaturamento: "5.500.000",
  //   leadTimes: [
  //     { categoria: "Blusa Manga Curta", dias: 120 },
  //     { categoria: "Blusa Básica", dias: 90 },
  //     { categoria: "Regata", dias: 90 },
  //     { categoria: "Top", dias: 90 },
  //     { categoria: "Camiseta", dias: 90 },
  //     { categoria: "Bermuda", dias: 120 },
  //     { categoria: "Calça", dias: 150 },
  //     { categoria: "Vestido", dias: 120 },
  //     { categoria: "Saia", dias: 120 },
  //     { categoria: "Jaqueta", dias: 180 },
  //   ],
  // };

  // qualidade de compra
  const lowTurnoverCtx = useMemo(() => {
    if (!inventoryDiagnosis) return null;

    const averagePercent = Number((inventoryDiagnosis?.summary?.avgLowTurnoverRate * 100).toFixed(1));

    const stores = (inventoryDiagnosis?.top5LowTurnover ?? []).map(s => ({
      name: s.storeName,
      percent: Number((Number(s.lowTurnoverRate) * 100).toFixed(1))
    }));

    const stoppedStockValue = inventoryDiagnosis?.summary.totalIdleStockValue;

    return { averagePercent, stores, stoppedStockValue };
  }, [inventoryDiagnosis]);

  const concentrationCtx = useMemo(() => {
    if (!inventoryDiagnosis) return null;

    const stores = (inventoryDiagnosis?.top5Concentration ?? []).map(s => ({
      name: s.storeName,
      volumePercent: Number((Number(s.top3pctVolumeConcentration) * 100).toFixed(1)),
      costPercent: Number((Number(s.top3pctCostConcentration) * 100).toFixed(1)),
    }));

    return { stores };
  }, [inventoryDiagnosis]);

  const stockBreakCtx = useMemo(() => {
    if (!inventoryDiagnosis) return null;

    const avgBreakProductCount = Math.round(inventoryDiagnosis?.summary?.avgBreakProductCount);
    const totalStockoutEstimatedLoss = inventoryDiagnosis?.summary?.totalStockoutEstimatedLoss;

    const stores = (inventoryDiagnosis?.top5BreakProductCount ?? []).map(s => ({
      name: s.storeName,
      value: Number(s.breakProductCount),
    }));

    return { avgBreakProductCount, totalStockoutEstimatedLoss, stores };
  }, [inventoryDiagnosis]);
  
  const purchaseOperationCtx = useMemo(() => {
    if (!purchaseOperation) return null;

    const stores = purchaseOperation.map(s => ({
      name: s.storeName,
      weeklyPurchases: s.ordersLastWeek,
      lastPurchaseDate: s.lastOrderDate
        ? new Date(s.lastOrderDate).toLocaleDateString('pt-BR')
        : '—',
      pendingPurchases: s.pendingOrdersCount,
      pendingValue: Number(s.pendingOrdersValue),
      deliveryTimePending: Boolean(s.leadTimeParameters),
      minStockPending: Boolean(s.minStockParameters),
    }));

    return { stores };
  }, [purchaseOperation]);
  
  return {
    actions: {
      // oportunidades
      setSelectedId,
      setRemovalReason,
      setOrder,
      handleRemoveOpportunity,
      handleFinishCheckoutOpportunity,
      resetModalState,
      setCheckoutSelectedProductIds,
      toggleTypeFilter,
      setActiveOppProductIds,
      setSorting: setSorting,
      
      // estoque
      setSelectedStore: setStoreId,
      setSeason: setSeason,
      setSearchLevel: setSearchLevel,
      setHorizon: setHorizon,

      definePatchOperationType: definePatchOperationType,
      
      resetOppError: () => setOppError(null),

      // open to buy
      // setSelectedCategory: setSelectedCategory,
      // setActiveView: setActiveView,
      toggleOtbSidebar: () => setOtbSidebarOpen(prev => !prev),

      // open to buy 
      saveOtbVersion: handleSaveOtbVersion,
      setSelectedCategory: setSelectedCategory,
      setSelectedCategoryName: setSelectedCategoryName,      
    },
    ctx: {
      // opportunities
      selectedId: selectedId,
      confirmationMessage: confirmationMessage,
      
      opportunitiesCount: opportunitiesCount,
      
      availableTypes: availableTypes,
      availableCheckoutTypes: availableCheckoutTypes,

      removalReason: removalReason,
      getOptions,

      opportunityCounterData: opportunityCounterData,

      opportunitiesTrends: opportunitiesTrends,
      opportunitiesTrendsDetails: opportunitiesTrendsDetails,

      activeOpportunities: activeOpportunities,
      selectedProductIds: activeProductIds,

      checkoutOpportunities: checkoutOpportunities,
      checkoutSelectedProductIds: checkoutSelectedProductIds,

      removedOpportunities: sortedRemovedOpportunities,

      opportunitiesDetails: opportunitiesDetails,

      // analytics
      merchandiseAnalytics: merchandiseAnalytics,

      // abastecimento
      fetchSupplyAdjustmentsData: fetchSupplyAdjustmentsData?.supplyAdjustments,
      supplyAdjustmentColumns: supplyAdjustmentsColumns,
      allStoresIds: fetchSupplyAdjustmentsData?.allStoresIds,      
      assortmentAreas: fetchSupplyAdjustmentsData?.areas,
      pagination: fetchSupplyAdjustmentsData?.pagination,
      hierarchyPath: fetchSupplyAdjustmentsData?.hierarchyPath,

      allStoresSupplyAdjustments: storesBreakdown,

      tableData: tableData,      
      selectedStore: storeId,      
      searchLevel: searchLevel,
      season: season,
      allAvailableSeasons: seasons,
      lostSalesRiskSum: inventory?.data?.lostSalesRisk,
      purchaseAdjustmentData: inventory?.data?.purchaseAdjustmentData,

      // estoques
      inventoryBubbleMetrics: bubbleMetricsData,
      inventoryVsSales: inventory?.data?.inventoryVsSales,
      horizons: horizons,
      horizon: horizon,
      medians: mediansForHorizon,

      inventoryDiagnosis: inventoryDiagnosis,
      lowTurnover: lowTurnoverCtx,
      concentration: concentrationCtx,
      stockBreak: stockBreakCtx,
      purchaseOperation: purchaseOperationCtx,

      // open to buy
      otbData: openToBuyDataConsolidated?.otbConsolidated?.otb,
      otbDataByCategory: openToBuyDataConsolidated?.otbConsolidated?.otbCategoryByUnits,
      otbDataByValue: openToBuyDataConsolidated?.otbConsolidated?.otbCategoryByValue,
      otbCompanyByCategory: openToBuyDataConsolidated?.otbConsolidated?.otbCategoryByUnits,
      otbCompanyByValue: openToBuyDataConsolidated?.otbConsolidated?.otbCategoryByValue,

      openToBuyByValueData: openToBuyDataConsolidated?.otbConsolidatedInventoryByValue?.otbConsolidatedInventoryByValue,
      openToBuyByUnitsData: openToBuyDataConsolidated?.otbConsolidatedInventoryByUnits?.otbConsolidatedInventoryByUnits,
      openToBuyAchievedData: openToBuyDataConsolidated?.achievedOtbConsolidated?.achievedOtbConsolidated,
      plannedOtbConsolidated: openToBuyDataConsolidated?.plannedOtbConsolidated?.plannedOtbConsolidated,

      otbInventoryDefinedByUnits: openToBuyDataConsolidated?.otbConsolidatedInventoryByUnits?.otbConsolidatedInventoryByUnits,
      achievedOtb: openToBuyDataConsolidated?.achievedOtbConsolidated?.achievedOtbConsolidated,

      categoryHierarchy: openToBuyDataConsolidated?.achievedOtbConsolidated?.categoryHierarchy,

      productHierarchy: openToBuyDataConsolidated?.achievedOtbConsolidated?.productHierarchy,
      categoryToGroupMap: openToBuyDataConsolidated?.achievedOtbConsolidated?.categoryToGroupMap,

      otbDetailByLevel: otbDetailByLevel,

      selectedCategory: selectedCategory,

      otbCycles: otbCycles,

      otbPerformanceByLevel: otbPerformanceByLevel,

      otbSalesPlan: otbSalesPlan?.salesPlan,
      optionsForFilterByLevelOnSalesPlan: otbSalesPlan?.filterValues,
      otbSalesPlanDetailed: otbSalesPlanDetailed,
      categoryHierarchyOnSalesPlan: otbSalesPlan?.categoryHierarchy,
      brands: otbSalesPlan?.brands,
    },
    uiState: {
      // oportunidades
      selected: selected,
      checkoutSelectedProductIds,
      
      removedOpportunities: sortedRemovedOpportunities || [],

      isUpdatingOpportunityStatus: isUpdatingOpportunityStatus,
      updateOppError: oppError,
      
      isLoadingOppCount: isLoadingOppCount,
      isLoadingCheckoutOpportunities: isLoadingCheckoutOpportunities,
      isLoadingActiveOpportunities: isLoadingActiveOpportunities,

      activeOpportunitiesError: activeOpportunitiesError,
      checkoutOpportunitiesError: checkoutOpportunitiesError,

      order: order,
      orderOptions: orderOptions,

      sortedActiveOpportunities: sortedActiveOpportunities || [],
      sortedCheckoutOpportunities: sortedCheckoutOpportunities || [],

      isLoadingOpportunitiesTrends: isLoadingOpportunitiesTrends,
      isLoadingOpportunitiesTrendsDetails: isLoadingOpportunitiesTrendsDetails,

      isLoadingOpportunitiesDetails: isLoadingOpportunitiesDetails,

      // abastecimento
      isLoadingSupplyAdjustments: isLoadingSupplyAdjustments,

      // estoques
      sorting: sorting,
      isLoadingInventoryDiagnosis: isLoadingInventoryDiagnosis,

      // open to buy
      isLoadingOtbData: isLoadingOtbDataConsolidated,
      isLoadingOtbDetail: isLoadingOtbDetail,
      isLoadingOtbCycles: isLoadingOtbCycles,
      isLoadingOtbPerformanceByLevel: isLoadingOtbPerformanceByLevel,
      isLoadingOtbSalesPlan: isLoadingOtbSalesPlan,
      isLoadingOtbSalesPlanDetailed: isLoadingOtbSalesPlanDetailed,

      // activeView: activeView,
      otbSidebarOpen: otbSidebarOpen,

      // open to buy — versionamento (NOVO)
      isSavingOtbVersion: isSavingOtbVersion,
      saveOtbVersionError: saveOtbVersionError,

      // analytics
      isLoadingMerchandiseAnalytics: isLoadingMerchandiseAnalytics,
      merchandiseAnalyticsError: merchandiseAnalyticsError,         
    }
  }
};